import { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

interface INavigationState {
  callback?: () => void;
  isNavigationBlocked: boolean;
}

export const useBlockNavigation = () => {
  const location = useLocation();
  const history = useHistory();

  const [navigationState, setNavigationState] = useState<INavigationState>({
    isNavigationBlocked: false,
  });

  const blockNavigation = useCallback(
    (value: boolean) => {
      if (value !== navigationState.isNavigationBlocked) {
        setNavigationState((state) => ({ ...state, isNavigationBlocked: value }));
      }
      if (navigationState.callback && !value) {
        setNavigationState((state) => ({ ...state, callback: undefined }));
      }
    },
    [navigationState.isNavigationBlocked, setNavigationState, navigationState.callback],
  );

  const setCallback = useCallback(
    (callback: any) => {
      setNavigationState((state) => ({ ...state, callback }));
    },
    [setNavigationState],
  );

  useEffect(() => {
    setNavigationState({ isNavigationBlocked: false, callback: undefined });
  }, [location]);

  useEffect(() => {
    const unblock = history.block((_, action) => {
      if (
        action === 'POP' &&
        typeof navigationState.callback === 'undefined' &&
        navigationState.isNavigationBlocked
      ) {
        setCallback(() => {
          unblock();
          history.goBack();
        });

        return false;
      }
    });
    return () => {
      unblock();
    };
  }, [navigationState.isNavigationBlocked, navigationState.callback]);

  return {
    blockNavigation,
    setCallback,
    isNavigationBlocked: navigationState.isNavigationBlocked,
    callback: navigationState.callback,
    isCallbackSet: !!navigationState.callback,
  };
};
